import { gql } from "@apollo/client/core";

const BILLING_ACCOUNT_COST_INFO_FRAGMENT = gql`
  fragment BillingAccountCostInfoFragment on BillingAccountCostInfo {
    costToDate
    costToDateGross
    productCostInfos {
      productID
      productName
      costToDate
      costToDateGross
    }
    serviceCostInfos {
      serviceID
      costToDateGross
    }
    billingPeriodStart
    billingPeriodEnd
  }
`;

const PROJECT_FRAGMENT = gql`
  fragment ProjectFragment on Project {
    id
    name
    maxVPCs
    created
    isOwner
    enforcementType
    queuedForDeletion
    loggedUserRole {
      role
      permissions {
        project {
          PROJECT_READ
          PROJECT_UPDATE
          PROJECT_BILLING_UPDATE
          PROJECT_MEMBERS_UPDATE
          PROJECT_OWNER_UPDATE
          PROJECT_PLAN_CHANGE
        }
      }
    }
    billingAccount {
      id
      created
      name
      permission {
        billingAccountId
        created
        userId
        role
      }
      allPermissions {
        userId
        billingAccountId
        created
        role
      }
      isUsingInvoicing
      trialEndDate
      trialStatus
      trialType
      trialUsage {
        endDate
        remainingUSD
      }
      paymentMethods {
        id
        isPrimary
        cardInfo {
          brand
          last4
          expMonth
          expYear
        }
        addressInfo {
          city
          state
          country
          line1
          line2
          postalCode
        }
      }
      addressDetails {
        companyAddress {
          companyName
          line1
          line2
          city
          state
          country
          postalCode
        }
      }
    }
    entitlementSpec {
      exporter {
        enabled
      }
      ha_replica {
        enabled
        max_instances_per_service
      }
      insights {
        enabled
        max_hours
      }
      invoice_billing {
        enabled
      }
      pitr {
        enabled
        max_days
      }
      plan_type
      support {
        email_support
        solution_architect_support
        production_support
      }
      read_replica {
        enabled
      }
      saml {
        enabled
      }
      services {
        enabled
        max_bandwidth_mb
        max_cpu
        max_cpu_milli
        max_instances
        max_fixed_instances
        max_iops
        max_memory
        max_memory_gb
        max_storage_gb
        min_bandwidth_mb
        min_iops
        unlimited
      }
      tiered_storage {
        enabled
      }
      tiger_lake {
        enabled
      }
      vpc {
        attachment_type
        enabled
        max_instances
      }
      ip_allow_list {
        enabled
        max_list_count
        max_cidr_blocks_count
      }
      transit_gateway {
        enabled
      }
      enhanced_iops {
        enabled
        min_iops
        max_iops
      }
      enhanced_storage {
        enabled
      }
    }
  }
`;

const SERVICE_FRAGMENT_BASE = gql`
  fragment ServiceFragmentBase on Service {
    name
    id
    projectId
    type
    servicePlan
    isReadOnly
    created
    regionCode
    versions {
      hasBackupInfo
      postgresVersion {
        name
        installed
        available
      }
      timescaleDBVersion {
        name
        installed
        available
      }
    }
    endpoints {
      primary {
        host
        port
      }
      replica {
        host
        port
      }
      pooler {
        host
        port
      }
    }
    storageType
    iops
    spec {
      ... on TimescaleDBServiceSpec {
        hostname
        username
        port
        defaultDBName
        replicaHostName
        replicaPort
        exporterID
        genericExporterID
        exporterSidecarEnabled
        connectionPoolerEnabled
        IOBoostEnabled
        poolerPort
        poolerHostName
        popSQLIntegrationEnabled
        isFixed
        status
      }
    }
    resources {
      id
      serviceId
      type
      spec {
        ... on ResourceNode {
          milliCPU
          memoryGB
          storageGB
          replicaCount
          syncReplicaCount
        }
      }
    }
    status
    vpcId
    privateLinkConnectionIds
    vpcEndpoint {
      ... on Endpoint {
        projectId
        serviceId
        vpcId
        host
        port
      }
    }
    metadata {
      environment
      isAI
    }
    dataTieringSettings {
      enabled
    }
    forkedFromId {
      serviceId
      projectId
      isStandby
      targetTime
    }
    primaryOrdinal
    replicaOrdinals
    replicaStatus
  }
`;

// Metrics fragment to be included optionally
const SERVICE_METRICS_FRAGMENT = gql`
  fragment ServiceMetricsFragment on Service {
    metrics {
      ... on ResourceMetrics {
        Time
        memoryMB
        storageMB
        storageBaseMB
        storageWalMB
        storageTmpMB
        milliCPU
      }
    }
  }
`;

const SERVICE_FRAGMENT = gql`
  ${SERVICE_FRAGMENT_BASE}
  ${SERVICE_METRICS_FRAGMENT}
  fragment ServiceFragment on Service {
    ...ServiceFragmentBase
    ...ServiceMetricsFragment
  }
`;

const GET_PROJECT__TYPE = "getProject";
const GET_PROJECT = gql`
  ${PROJECT_FRAGMENT}
  query GetProject($id: ID!) {
    getProject(id: $id) {
      ...ProjectFragment
    }
  }
`;

const GET_USER = gql`
  query GetUser {
    getUser {
      id
      name
      email
      phone
      created
      managedByIdp
      mfaMethods {
        ... on AuthenticatorMethod {
          id
        }
      }
      origin
      uiState
      currentAuthMethod
      queuedForDeletion
    }
  }
`;

const GET_USER__TYPE = "getUser";

const GET_ALL_PROJECTS__TYPE = "getAllProjects";

const GET_ALL_PROJECTS = gql`
  query GetAllProjects {
    getAllProjects {
      id
      name
      isOwner
      enforcementType
      billingAccount {
        id
      }
      entitlementSpec {
        plan_type
      }
      loggedUserRole {
        role
      }
      queuedForDeletion
    }
  }
`;

const GET_BILLING_ACCOUNT = gql`
  query GetBillingAccountCorporateInvoicing($projectId: ID!) {
    getProject(id: $projectId) {
      id
      billingAccount {
        id
        isUsingInvoicing
        trialEndDate
        paymentMethodInfo {
          primaryType
        }
        paymentMethods {
          id
          isPrimary
          cardInfo {
            brand
            last4
            expMonth
            expYear
          }
          addressInfo {
            city
            state
            country
            line1
            line2
            postalCode
          }
        }
        addressDetails {
          companyAddress {
            companyName
            line1
            line2
            city
            state
            country
            postalCode
          }
        }
        trialStatus
        trialType
        trialUsage {
          endDate
          remainingUSD
        }
      }
      entitlementSpec {
        plan_type
      }
    }
  }
`;

const GET_BILLING_EMAILS = gql`
  query GetBillingEmailAddresses($billingAccountId: ID!) {
    getBillingEmailAddresses(billingAccountId: $billingAccountId) {
      id
      email
      billingAccountId
      created
      activated
    }
  }
`;

const GET_BILLING_ACCOUNT__TYPE = "getProject";

const INVOICE_FRAGMENT = gql`
  fragment InvoiceFragment on Invoice {
    id
    created
    updated
    billingAccountId
    totalCharged
    paymentState
    pdfUrl
    fromInternalBilling
  }
`;

const GET_ORB_INVOICES__TYPE = "getOrbInvoices";

const GET_ORB_INVOICES = gql`
  ${INVOICE_FRAGMENT}
  query GetOrbInvoices($billingAccountId: ID!, $year: Int) {
    getOrbInvoices(billingAccountId: $billingAccountId, year: $year) {
      invoices {
        ...InvoiceFragment
      }
      unpaidInvoices {
        ...InvoiceFragment
      }
    }
  }
`;

const GET_PAYMENT_METHODS__TYPE = "getPaymentMethods";

const GET_PAYMENT_METHODS = gql`
  query GetPaymentMethods($billingAccountId: ID!) {
    getPaymentMethods(billingAccountId: $billingAccountId) {
      id
      isPrimary
      type
      cardInfo {
        brand
        last4
        expMonth
        expYear
      }
      addressInfo {
        city
        state
        country
        line1
        line2
        postalCode
      }
    }
  }
`;

// Note, you almost certain want to keep GET_SERVICE and GET_ALL_SERVICES in sync
const GET_SERVICE__TYPE = "getService";
const GET_SERVICE = gql`
  ${SERVICE_FRAGMENT}
  query GetService($serviceId: ID!, $projectId: ID!) {
    getService(data: { serviceId: $serviceId, projectId: $projectId }) {
      ...ServiceFragment
    }
  }
`;

const GET_SERVICE_RESOURCES_AND_STATUS = gql`
  query GetServiceResourcesAndStatus($serviceId: ID!, $projectId: ID!) {
    getService(data: { serviceId: $serviceId, projectId: $projectId }) {
      id
      name
      status
      resources {
        spec {
          ... on ResourceNode {
            storageGB
            milliCPU
            memoryGB
            replicaCount
            syncReplicaCount
          }
        }
      }
    }
  }
`;

const GET_SERVICE_RESIZE_INFO = gql`
  query GetServiceResizeInfo($serviceId: ID!, $projectId: ID!) {
    getService(data: { serviceId: $serviceId, projectId: $projectId }) {
      id
      name
      status
      resources {
        id
        type
        serviceId
        spec {
          ... on ResourceNode {
            storageGB
            milliCPU
            memoryGB
            replicaCount
            syncReplicaCount
          }
        }
      }
    }
  }
`;

const SERVICE_METRICS_BASE_FRAGMENT = gql`
  fragment ServiceMetricsBaseFields on ServiceMetrics {
    compute {
      ordinal
      role
      limits {
        milliCPU
        memoryMiB
      }
      data {
        time
        milliCPU
        memoryMiB
      }
    }
    storage {
      ordinal
      role
      data {
        time
        storageMiB
      }
    }
    diskPerformance {
      ordinal
      role
      limits {
        iops
        bandwidthMBs
      }
      data {
        time
        readIOPs
        writeIOPs
        readBandwidth
        writeBandwidth
      }
    }
  }
`;

const GET_SERVICE_METRICS = gql`
  ${SERVICE_METRICS_BASE_FRAGMENT}
  query GetServiceMetrics(
    $serviceId: ID!
    $projectId: ID!
    $start: Time!
    $end: Time!
    $bucketSeconds: Int!
    $role: InstanceRole
  ) {
    getServiceMetrics(
      data: {
        serviceId: $serviceId
        projectId: $projectId
        start: $start
        end: $end
        bucketSeconds: $bucketSeconds
        role: $role
      }
    ) {
      ...ServiceMetricsBaseFields
      events {
        time
        type
        ordinal
      }
    }
  }
`;

// queriesPerSecond is fetched separately from the base metrics above. It is a
// non-null field on a non-null ServiceMetrics, so a QPS timeout would null the
// whole payload and blank every chart (Support-Dev-Collab#4462). Splitting it
// into its own request contains a QPS failure to just the QPS chart.
const GET_SERVICE_METRICS_QPS = gql`
  query GetServiceMetricsQps(
    $serviceId: ID!
    $projectId: ID!
    $start: Time!
    $end: Time!
    $bucketSeconds: Int!
    $role: InstanceRole
  ) {
    getServiceMetrics(
      data: {
        serviceId: $serviceId
        projectId: $projectId
        start: $start
        end: $end
        bucketSeconds: $bucketSeconds
        role: $role
      }
    ) {
      queriesPerSecond {
        ordinal
        role
        data {
          time
          reads
          writes
          all
        }
      }
    }
  }
`;

const GET_POPSQL_INTEGRATION_STATUS__TYPE = "getPopSQLIntegrationStatus";
const GET_POPSQL_INTEGRATION_STATUS = gql`
  query GetPopSQLIntegrationStatus($serviceId: ID!, $projectId: ID!) {
    getPopSQLIntegrationStatus(
      data: { projectId: $projectId, serviceId: $serviceId }
    )
  }
`;

// Note, you almost certain want to keep GET_SERVICE and GET_ALL_SERVICES in sync
const GET_ALL_SERVICES__TYPE = "getAllServices";
const GET_ALL_SERVICES = gql`
  ${SERVICE_FRAGMENT}
  query GetAllServices($projectId: ID!) {
    getAllServices(projectId: $projectId) {
      ...ServiceFragment
    }
  }
`;

const GET_SERVICE_LOGS__TYPE = "getServiceLogs";
const GET_SERVICE_LOGS = gql`
  query GetServiceLogs(
    $serviceId: ID!
    $projectId: ID!
    $serviceOrdinal: Int
    $pageIndex: Int
    $startTime: Time!
    $searchTags: [String]
  ) {
    getServiceLogs(
      data: {
        serviceId: $serviceId
        projectId: $projectId
        serviceOrdinal: $serviceOrdinal
        pageIndex: $pageIndex
        startTime: $startTime
        searchTags: $searchTags
      }
    )
  }
`;

const SERVICE_LOGS_PAGE = gql`
  query ServiceLogsPage(
    $serviceId: ID!
    $projectId: ID!
    $filters: ServiceLogsFilters
    $cursor: ID
    $limit: Int!
    $bucketIntervalSeconds: Int
    $fields: [String!]
  ) {
    serviceLogs(
      serviceId: $serviceId
      projectId: $projectId
      filters: $filters
    ) {
      aggregates {
        countsBySeverity(bucketIntervalSeconds: $bucketIntervalSeconds) {
          totals {
            severity
            count
          }
          dataPoints {
            bucket
            counts {
              severity
              count
            }
          }
        }
      }
      items(cursor: $cursor, limit: $limit, fields: $fields) {
        items {
          cursor
          item {
            timestamp
            message
            severity
            fields {
              key
              value
            }
          }
        }
        pageInfo {
          lastCursor
          hasNextPage
        }
      }
    }
  }
`;

const LOGIN_BASIC = gql`
  query LoginMfa($email: String!, $password: String!) {
    loginMfa(
      data: { basic: { email: $email, password: $password }, noJWT: true }
    ) {
      ... on LoginSuccess {
        user {
          id
          name
          email
          phone
          created
        }
      }
      ... on LoginMfaRequired {
        mfaMethods {
          ... on AuthenticatorMethod {
            id
          }
        }
        twoFactorId
      }
    }
  }
`;

const LOGIN_MFA_CHALLENGE = gql`
  query LoginMfaChallenge($code: String!, $twoFactorId: String!) {
    loginMfa(
      data: { noJWT: true, mfa: { code: $code, twoFactorId: $twoFactorId } }
    ) {
      ... on LoginSuccess {
        user {
          id
          name
          email
          phone
          created
        }
      }
    }
  }
`;

const MFA_CHALLENGE = gql`
  query MfaChallenge(
    $code: String!
    $trustChallenge: String!
    $resetToken: String
  ) {
    mfaChallenge(
      data: {
        code: $code
        trustChallenge: $trustChallenge
        resetToken: $resetToken
      }
    ) {
      trustToken
    }
  }
`;

const LOGOUT = gql`
  query Logout {
    logout
  }
`;

const GET_ORB_PRODUCTS = gql`
  query GetOrbProducts($projectId: ID!, $experimentGroup: ExperimentGroup) {
    orbProducts(projectId: $projectId, experimentGroup: $experimentGroup) {
      id
      name
      plans {
        ... on Plan {
          productId
          IOPS
          price
          milliCPU
          memoryGB
          storageGB
          regionCode
          cloudProvider
        }
      }
    }
  }
`;

const GET_ORB_ALL_PRODUCTS = gql`
  query GetOrbAllProducts {
    orbAllProducts {
      id
      products {
        id
        name
        plans {
          ... on Plan {
            productId
            IOPS
            price
            milliCPU
            memoryGB
            storageGB
            regionCode
            cloudProvider
          }
        }
      }
    }
  }
`;

const GET_SERVICE_CONFIG_HISTORY = gql`
  query GetServiceConfigHistory(
    $serviceId: ID!
    $projectId: ID!
    $serviceOrdinal: Int
  ) {
    getServiceConfigHistory(
      projectId: $projectId
      serviceId: $serviceId
      serviceOrdinal: $serviceOrdinal
    ) {
      compute {
        ... on ServiceComputeConfig {
          memoryGB
          milliCPU
          created
        }
      }
      storage {
        ... on ServiceStorageConfig {
          storageGB
          volumeType
          iops
          bandwidthMBs
          created
        }
      }
      ServiceStorageReadyToBeModified
    }
  }
`;

const VALIDATE_RESET_TOKEN_MFA = gql`
  query ValidateResetTokenMfa($token: String!) {
    validateResetTokenMfa(token: $token) {
      mfaMethods {
        ... on AuthenticatorMethod {
          id
        }
      }
      message
    }
  }
`;

const GET_POSTGRES_PARAMETERS__TYPE = "getPostgresParameters";
const GET_POSTGRES_PARAMETERS = gql`
  query GetPostgresParameters($serviceId: ID!, $projectId: ID!) {
    getPostgresParameters(
      data: { projectId: $projectId, serviceId: $serviceId }
    ) {
      string_parameters {
        info {
          name
          description
          is_user_editable
          requires_restart
          is_tune_overridable
          ui_priority
          is_pending_restart
          is_user_modified
        }
        current_value
        allowed_values
      }
      numeric_parameters {
        info {
          name
          description
          is_user_editable
          requires_restart
          is_tune_overridable
          ui_priority
          is_pending_restart
          is_user_modified
        }
        unit
        current_value
        max_allowed_value
        min_allowed_value
      }
    }
  }
`;

const GET_ALL_NEXT_MAINTENANCE = gql`
  query GetAllNextMaintenance($projectId: ID!) {
    getAllNextMaintenance(projectId: $projectId) {
      maintenance {
        start
        end
        isMaintenanceScheduled
      }
      serviceId
    }
  }
`;

const GET_SERVICE_TABLES = gql`
  query GetServiceTables(
    $projectId: ID!
    $serviceId: ID!
    $regionCode: String
  ) {
    getTableList(
      data: {
        projectId: $projectId
        serviceId: $serviceId
        regionCode: $regionCode
      }
    ) {
      name
      isHypertable
      schema
    }
  }
`;

const GET_SERVICE_CATALOG_TABLES__TYPE = `getCatalogTableList`;
const GET_SERVICE_CATALOG_TABLES = gql`
  query GetServiceCatalogTables(
    $projectId: ID!
    $serviceId: ID!
    $regionCode: String
  ) {
    getCatalogTableList(
      data: {
        projectId: $projectId
        serviceId: $serviceId
        regionCode: $regionCode
      }
    ) {
      name
      isHypertable
      schema
    }
  }
`;

const GET_SERVICE_CONTINUOUS_AGGREGATES__TYPE = `getContAggList`;
const GET_SERVICE_CONTINUOUS_AGGREGATES = gql`
  query GetServiceContinuousAggregates(
    $projectId: ID!
    $serviceId: ID!
    $regionCode: String
  ) {
    getContAggList(
      data: {
        projectId: $projectId
        serviceId: $serviceId
        regionCode: $regionCode
      }
    ) {
      name
      schema
    }
  }
`;

const GET_SERVICE_LARGEST_OBJECTS__TYPE = `getLargestObjects`;
const GET_SERVICE_LARGEST_OBJECTS = gql`
  query GetServiceLargestObjects(
    $projectId: ID!
    $serviceId: ID!
    $regionCode: String
    $limit: Int
  ) {
    getLargestObjects(
      data: {
        projectId: $projectId
        serviceId: $serviceId
        regionCode: $regionCode
        limit: $limit
      }
    ) {
      objectId: id {
        __typename
        ... on TableID {
          isHypertable
          schema
          name
        }
        ... on ContAggID {
          name
          schema
        }
      }
      size
    }
  }
`;

const GET_SERVICE_GENERAL_INFORMATION__TYPE = `getDatabaseOverview`;
const GET_SERVICE_GENERAL_INFORMATION = gql`
  query GetServiceGeneralInformation(
    $projectId: ID!
    $serviceId: ID!
    $regionCode: String
  ) {
    getDatabaseOverview(
      data: {
        projectId: $projectId
        serviceId: $serviceId
        regionCode: $regionCode
      }
    ) {
      info: objectCounts {
        pgTables
        hypertables
        contAggs
        realtimeContAggs
        policies
      }
      compressionV2 {
        numCompressedHypertables
        numCompressedContAggs
        stats {
          precompressed
          compressed
          uncompressed
          diskSize
          originalSize
          savings
        }
      }
      policyCounts {
        name
        count
      }
    }
  }
`;

const HYPER_METADATA_FRAGMENT = gql`
  fragment HyperMetadataFragment on HyperMetadata {
    hypertableId
    numChunks
    compression
    compressionStats {
      ... on CompressionStats {
        precompressed
        compressed
        uncompressed
        diskSize
        originalSize
        savings
      }
    }
    timeInterval
    timeIntervalUsecs
    integerInterval
    dimensions {
      ... on TimeDimension {
        columnName
        timeInterval
        integerInterval
      }
      ... on SpaceDimension {
        columnName
        numPartitions
      }
    }
  }
`;

const GET_SERVICE_TABLE_INFO__TYPE = `getTableInfo`;

const GET_SERVICE_TABLE_INFO = gql`
  ${HYPER_METADATA_FRAGMENT}
  query GetTableInfo(
    $projectId: ID!
    $serviceId: ID!
    $tableSchema: ID!
    $tableName: ID!
    $regionCode: String
  ) {
    getTableInfo(
      data: {
        projectId: $projectId
        serviceId: $serviceId
        tableSchema: $tableSchema
        tableName: $tableName
        regionCode: $regionCode
      }
    ) {
      metadata {
        id {
          schema
          name
          isHypertable
        }
        size
        approxRowCount
        owner
      }
      hyperMetadata {
        ...HyperMetadataFragment
      }
      schema {
        name
        type
        default
        nullable
      }
      indexes {
        name
        definition
      }
      chunks {
        name
        size
        startTime
        endTime
        rangeStart
        rangeEnd
        isCompressed
        compressedSize
        precompressedSize
        uncompressedSize
        indexSize
      }
      contAggs {
        name
        schema
        materializedOnly
        definition
      }
      foreignKeys {
        name
        keyCols
        refSchema
        refName
        refCols
      }
      policies {
        id
        type
        target
        interval
        fullInfo
      }
    }
  }
`;

const GET_SERVICE_CAGG_INFO__TYPE = `getContAggInfo`;
const GET_SERVICE_CAGG_INFO = gql`
  ${HYPER_METADATA_FRAGMENT}
  query GetServiceCaggInfo(
    $projectId: ID!
    $serviceId: ID!
    $contAggSchema: ID!
    $contAggName: ID!
    $regionCode: String
  ) {
    getContAggInfo(
      data: {
        projectId: $projectId
        serviceId: $serviceId
        contAggSchema: $contAggSchema
        contAggName: $contAggName
        regionCode: $regionCode
      }
    ) {
      metadata {
        id {
          schema
          name
        }
        size
        approxRowCount
        owner
        materializedOnly
        lastRefresh
        nextRefresh
        parentID {
          ... on TableID {
            schema
            name
          }
          ... on ContAggID {
            schema
            name
          }
        }
      }
      hyperMetadata {
        ...HyperMetadataFragment
      }
      schema {
        name
        type
        default
        nullable
        constraints
      }
      indexes {
        name
        definition
      }
      definition
      policies {
        id
        type
        target
        interval
        fullInfo
      }
      refreshPolicies {
        startOffset
        endOffset
        interval
      }
      chunks {
        name
        size
        startTime
        endTime
        rangeStart
        rangeEnd
        isCompressed
        compressedSize
        precompressedSize
        uncompressedSize
        indexSize
      }
    }
  }
`;

const GET_ALL_VPC = gql`
  query GetAllVpcs($projectId: ID!) {
    getAllVpcs(projectId: $projectId) {
      id
      projectId
      provisionedId
      cidr
      cloudProvider
      name
      created
      updated
      peeringConnections {
        id
        vpcId
        peerVpc {
          id
          accountId
          regionCode
          cidr
          cidrBlocks
        }
        errorMessage
        status
      }
      errorMessage
      status
      regionCode
    }
  }
`;

const GET_ALL_IP_ALLOW_LIST = gql`
  query GetAllIpAllowList($projectId: ID!) {
    getIPAllowLists(projectID: $projectId) {
      listId
      description
      cidrBlocks
      associatedServices
      created
    }
  }
`;

const GET_PROJECT_MEMBERS = gql`
  query GetProjectMembers($projectId: ID!) {
    getProjectMembers(projectId: $projectId) {
      projectId
      userId
      hasSSO
      user {
        id
        name
        email
        phone
        mfaMethods {
          ... on AuthenticatorMethod {
            id
          }
        }
        lastLoginSession {
          timestamp
          authMethod
        }
        created
      }
      joined
      role
      rbacRole
    }
  }
`;

const GET_PROJECT_INVITES = gql`
  query GetProjectInvites($projectId: ID!) {
    getProjectInvites(projectId: $projectId) {
      id
      project {
        id
        name
      }
      email
      role
      created
      declined
      rbacRole
    }
  }
`;

const GET_PROJECT_INVITES_FOR_USER = gql`
  query GetProjectInvitesForUser {
    getProjectInvitesForUser {
      id
      project {
        id
        name
      }
      inviter {
        id
        name
        email
        phone
      }
      email
      role
      created
      declined
      hash
      rbacRole
    }
  }
`;

const GET_PENDING_PAYMENT_DELETION__TYPE = "getPendingPaymentDeletion";

const GET_PENDING_PAYMENT_DELETION = gql`
  query GetPendingPaymentDeletion($billingAccountId: ID!) {
    getPendingPaymentDeletion(billingAccountId: $billingAccountId) {
      id
    }
  }
`;

const GET_SERVICE_POSTGRES_AND_TIMESCALEDB_VERSIONS = gql`
  query GetPostgresAndTimescaleDBVersion($projectId: ID!, $serviceId: ID!) {
    getPostgresAndTimescaleDBVersion(
      data: { projectId: $projectId, serviceId: $serviceId }
    ) {
      postgresVersion {
        name
        installed
        available
      }
      timescaleDBVersion {
        name
        installed
        available
        scheduledUpgrade
      }
      hasBackupInfo
    }
  }
`;
const GET_ALL_METRIC_EXPORTERS__TYPE = "getAllMetricExporters";

const GET_ALL_METRIC_EXPORTERS = gql`
  query GetAllMetricExporters($projectId: ID!) {
    getAllMetricExporters(projectId: $projectId) {
      id
      exporterUuid
      projectId
      created
      name
      type
      regionCode
      config {
        ... on CloudWatchMetricConfig {
          logGroupName
          logStreamName
          namespace
          awsRegion
          awsRoleArn
          awsAccessKey
          awsSecretKey
        }
        ... on DatadogMetricConfig {
          apiKey
          site
        }
        ... on PrometheusMetricConfig {
          user
          password
        }
        ... on AzureMonitorMetricConfig {
          connectionString
        }
      }
      enablePgMetrics
    }
  }
`;

const GET_ALL_GENERIC_EXPORTERS__TYPE = "getAllGenericExporters";

const GET_ALL_GENERIC_EXPORTERS = gql`
  query GetAllGenericExporters($projectId: ID!) {
    getAllGenericExporters(projectId: $projectId) {
      id
      projectId
      created
      name
      type
      regionCode
      config {
        ... on CloudWatchConfig {
          logGroupName
          logStreamName
          awsRegion
          awsRoleArn
          awsAccessKey
          awsSecretKey
        }
      }
    }
  }
`;

const GET_SERVICE_POSTGRES_MAJOR_VERSION_UPGRADE_STATUS = gql`
  query GetPostgresMajorVersionUpgradeStatus($projectId: ID!, $serviceId: ID!) {
    getPostgresMajorVersionUpgradeStatus(
      data: { projectId: $projectId, serviceId: $serviceId }
    ) {
      stage
      error
    }
  }
`;

const GET_PAT_RECORDS__TYPE = "getPATRecords";
const GET_PAT_RECORDS = gql`
  query GetPATRecords($projectId: ID!) {
    getPATRecords(projectID: $projectId) {
      clientCredentials {
        accessKey
        secretKey
      }
      name
      projectId
      created
      issuingUserId
    }
  }
`;

const GET_POSTGRES_AVAILABLE_EXTENSIONS = gql`
  query GetPostgresAvailableExtensions(
    $projectId: ID!
    $serviceId: ID!
    $regionCode: String
  ) {
    getPostgresAvailableExtensions(
      data: {
        projectId: $projectId
        serviceId: $serviceId
        regionCode: $regionCode
      }
    ) {
      name
      defaultVersion
      installedVersion
      comment
    }
  }
`;

const GET_DATA_TIERING_SUMMARY__TYPE = "getDataTieringSummary";
const GET_DATA_TIERING_SUMMARY = gql`
  query GetDataTieringSummary($projectId: ID!) {
    getDataTieringSummary(projectId: $projectId) {
      serviceIds
      tieredBytes
    }
  }
`;

const GET_COSTED_PROJECT_DATA_TIERING_USAGE_DISTRIBUTION__TYPE =
  "getCostedProjectDataTieringUsageDistribution";
const GET_COSTED_PROJECT_DATA_TIERING_USAGE_DISTRIBUTION = gql`
  query GetCostedProjectDataTieringUsageDistribution($projectId: ID!) {
    getCostedProjectDataTieringUsageDistribution(projectID: $projectId) {
      serviceIds
      tieringUsageInBytes
      tieringUsageCost
      estimatedEbsUsageInBytes
      estimatedEbsUsageCost
    }
  }
`;

const GET_RECOVERY_WINDOWS__TYPE = "getRecoveryWindows";
const GET_RECOVERY_WINDOWS = gql`
  query GetRecoveryWindows($projectId: ID!, $serviceId: ID!) {
    getRecoveryWindows(data: { projectId: $projectId, serviceId: $serviceId }) {
      method
      startTime
      endTime
    }
  }
`;

const GET_QUERY_STATS__TYPE = "getQueryStatsAndMetadata";
const GET_QUERY_STATS = gql`
  fragment metricValue on MetricValue {
    value
    unit
  }
  query GetQueryStats(
    $projectId: ID!
    $serviceId: ID!
    $serviceRole: InstanceRole
    $limit: Int!
    $sortBy: QueryStatSortFieldOption!
    $sortOrder: QueryStatSortOrderOption!
    $constraints: [QueryStatConstraint!]
    $queryRange: QueryRange!
  ) {
    getQueryStatsAndMetadata(
      data: {
        projectId: $projectId
        serviceId: $serviceId
        serviceRole: $serviceRole
        limit: $limit
        sortBy: $sortBy
        sortOrder: $sortOrder
        constraints: $constraints
        queryRange: $queryRange
      }
    ) {
      stats {
        queryHash
        query
        userName
        hasExplainPlan
        metrics {
          executions {
            ...metricValue
          }
          rows {
            ...metricValue
          }
          p50Time {
            ...metricValue
          }
          p95Time {
            ...metricValue
          }
          totalTime {
            ...metricValue
          }
          hypertablesCount {
            ...metricValue
          }
          compressionCount {
            ...metricValue
          }
          # CPU
          p50CpuTimeUser {
            ...metricValue
          }
          p95CpuTimeUser {
            ...metricValue
          }
          p50CpuTimeSys {
            ...metricValue
          }
          p95CpuTimeSys {
            ...metricValue
          }
          # Memory
          p50MemAllocatedBytes {
            ...metricValue
          }
          p95MemAllocatedBytes {
            ...metricValue
          }
          # I/O
          p50FsBytesRead {
            ...metricValue
          }
          p95FsBytesRead {
            ...metricValue
          }
          p50FsBytesWritten {
            ...metricValue
          }
          p95FsBytesWritten {
            ...metricValue
          }
        }
      }
      queriesDataPoints {
        queryHash
        dataPoints {
          bucket
          metrics {
            p50Time {
              value
              unit
            }
            p95Time {
              value
              unit
            }
          }
        }
      }
      statsAsOf
    }
  }
`;

const METRIC_VALUE_FRAGMENT = gql`
  fragment graphMetricValue on MetricValue {
    value
    unit
  }
`;

const GRAPH_METRICS_FRAGMENT = gql`
  fragment graphMetrics on QueryStatMetrics {
    executions {
      ...graphMetricValue
    }
    plans {
      ...graphMetricValue
    }
    rows {
      ...graphMetricValue
    }
    p50Time {
      ...graphMetricValue
    }
    p95Time {
      ...graphMetricValue
    }
    cacheHitRatio {
      ...graphMetricValue
    }
    cacheMemoryRate {
      ...graphMetricValue
    }
    bytesReadRate {
      ...graphMetricValue
    }
    p50CpuTimeUser {
      ...graphMetricValue
    }
    p95CpuTimeUser {
      ...graphMetricValue
    }
    p50CpuTimeSys {
      ...graphMetricValue
    }
    p95CpuTimeSys {
      ...graphMetricValue
    }
    p50MemAllocatedBytes {
      ...graphMetricValue
    }
    p95MemAllocatedBytes {
      ...graphMetricValue
    }
    p50FsBytesRead {
      ...graphMetricValue
    }
    p95FsBytesRead {
      ...graphMetricValue
    }
    p50FsBytesWritten {
      ...graphMetricValue
    }
    p95FsBytesWritten {
      ...graphMetricValue
    }
  }
  ${METRIC_VALUE_FRAGMENT}
`;

const GET_QUERY_METRICS_GRAPHS = gql`
  query GetQueryMetricGraphs(
    $projectId: ID!
    $serviceId: ID!
    $serviceOrdinal: Int!
    $queryRange: QueryRange!
    $queryHash: String!
  ) {
    getQueryMetricGraphs(
      data: {
        projectId: $projectId
        serviceId: $serviceId
        serviceOrdinal: $serviceOrdinal
        queryRange: $queryRange
        queryHash: $queryHash
      }
    ) {
      total
      dataPoints {
        bucket
        queryHash
        metrics {
          ...graphMetrics
        }
      }
    }
  }
  ${GRAPH_METRICS_FRAGMENT}
`;
const GET_QUERY_METRICS_GRAPHS__TYPE = "getQueryMetricGraphs";

const GET_QUERY_DETAILS = gql`
  query GetQueryDetails(
    $projectId: ID!
    $serviceId: ID!
    $serviceOrdinal: Int!
    $queryHash: String!
  ) {
    getQueryDetails(
      data: {
        projectId: $projectId
        serviceId: $serviceId
        serviceOrdinal: $serviceOrdinal
        queryHash: $queryHash
      }
    ) {
      queryDetails {
        normalizedQuery
      }
    }
  }
`;

const GET_LATEST_EXPLAIN__TYPE = "getQueryLatestExplain";
const GET_LATEST_EXPLAIN = gql`
  query GetQueryLatestExplain(
    $projectId: ID!
    $serviceId: ID!
    $serviceRole: InstanceRole!
    $queryHash: String!
  ) {
    getQueryLatestExplain(
      data: {
        projectId: $projectId
        serviceId: $serviceId
        serviceRole: $serviceRole
        queryHash: $queryHash
      }
    ) {
      plan
      timestamp
    }
  }
`;

const GET_QUERY_METRICS_DETAILS_AND_EXPLAIN__TYPE =
  "getQueryMetricsDetailsAndExplain";
const GET_QUERY_METRICS_DETAILS_AND_EXPLAIN = gql`
  query GetQueryMetricsDetailsAndExplain(
    $projectId: ID!
    $serviceId: ID!
    $serviceOrdinal: Int!
    $queryRange: QueryRange!
    $queryHash: String!
    $serviceRole: InstanceRole!
  ) {
    getQueryMetricGraphs(
      data: {
        projectId: $projectId
        serviceId: $serviceId
        serviceOrdinal: $serviceOrdinal
        serviceRole: $serviceRole
        queryRange: $queryRange
        queryHash: $queryHash
      }
    ) {
      total
      dataPoints {
        bucket
        queryHash
        metrics {
          ...graphMetrics
        }
      }
    }
    getQueryDetails(
      data: {
        projectId: $projectId
        serviceId: $serviceId
        serviceOrdinal: $serviceOrdinal
        queryHash: $queryHash
      }
    ) {
      queryDetails {
        normalizedQuery
      }
    }
    getQueryLatestExplain(
      data: {
        projectId: $projectId
        serviceId: $serviceId
        serviceRole: $serviceRole
        queryHash: $queryHash
      }
    ) {
      plan
      timestamp
    }
  }
  ${GRAPH_METRICS_FRAGMENT}
`;

const GET_CONNECTION_DETAILS__TYPE = "explorer";
const GET_CONNECTION_DETAILS = gql`
  query GetConnectionDetails($projectId: String!, $serviceId: String!) {
    explorer(projectId: $projectId, serviceId: $serviceId) {
      currentActivity {
        items {
          item {
            pid
            applicationName
            isInternal
            username
            state
            query
            queryStartedAt
            runningDurationUsecs
          }
        }
        aggregates {
          connectionsCount
          maxUsableConnections
          maxConnectionsSetting
          reservedConnections
        }
      }
    }
  }
`;

const GET_CONNECTION_AGGREGATES = gql`
  query GetConnectionDetailsAggregates(
    $projectId: String!
    $serviceId: String!
  ) {
    explorer(projectId: $projectId, serviceId: $serviceId) {
      currentActivity {
        aggregates {
          connectionsCount
          maxUsableConnections
          maxConnectionsSetting
          reservedConnections
        }
      }
    }
  }
`;

const GET_HISTORICAL_CONNECTIONS = gql`
  query GetHistoricalConnections(
    $projectId: String!
    $serviceId: String!
    $start: Time!
  ) {
    explorer(projectId: $projectId, serviceId: $serviceId) {
      historicalConnections(start: $start) {
        time
        maxUsableConnections
        connectionsCount
      }
    }
  }
`;

// Four aliased getSeries selections in one round trip instead of four
// sibling queries. name/fn are literals, not variables — this query is
// purpose-built for this graph, not metric-name-agnostic.
//
// connectionsCount gets its own filters variable, separate from the shared
// $filters (role only): it additionally excludes privileged roles (see
// PRIVILEGED_USERNAME_FILTERS), which the other three, labelless-by-usename
// pg_settings_* metrics don't need.
const GET_HISTORICAL_CONNECTIONS_NEW = gql`
  query GetHistoricalConnectionsNew(
    $projectId: ID!
    $serviceId: ID!
    $start: Time!
    $end: Time!
    $bucketSeconds: Int!
    $filters: [MetricLabelFilter!]
    $connectionsCountFilters: [MetricLabelFilter!]
  ) {
    getServiceMetrics(
      data: {
        projectId: $projectId
        serviceId: $serviceId
        start: $start
        end: $end
        bucketSeconds: $bucketSeconds
      }
    ) {
      connectionsCount: getSeries(
        name: "pg_stat_activity_count"
        fn: MAX_TOTAL
        filters: $connectionsCountFilters
      ) {
        dataPoints {
          time
          value
        }
      }
      maxConnections: getSeries(
        name: "pg_settings_max_connections"
        fn: LAST
        filters: $filters
      ) {
        dataPoints {
          time
          value
        }
      }
      reservedConnections: getSeries(
        name: "pg_settings_reserved_connections"
        fn: LAST
        filters: $filters
      ) {
        dataPoints {
          time
          value
        }
      }
      superuserReservedConnections: getSeries(
        name: "pg_settings_superuser_reserved_connections"
        fn: LAST
        filters: $filters
      ) {
        dataPoints {
          time
          value
        }
      }
    }
  }
`;

const GET_QUERY_DETAILS__TYPE = "getQueryDetails";

const GET_JOB_LIST = gql`
  query GetJobList(
    $projectId: String!
    $serviceId: String!
    $regionCode: String
    $filters: JobsFilters
    $executionsLimit: Int
    $executionsStart: Time
    $executionsEnd: Time
  ) {
    explorer(
      projectId: $projectId
      serviceId: $serviceId
      regionCode: $regionCode
    ) {
      jobs(filters: $filters) {
        items {
          item {
            id
            type
            currentStart
            nextStart
            status
            executions(
              n: $executionsLimit
              start: $executionsStart
              end: $executionsEnd
            ) {
              startTime
              finishTime
              succeeded
            }
            proc {
              name
              schema
            }
            target {
              ... on TableID {
                isHypertable
                schema
                name
              }
              ... on ContAggID {
                name
                schema
              }
            }
          }
        }
      }
    }
  }
`;

const GET_JOB_BY_ID = gql`
  query GetJobById(
    $projectId: String!
    $serviceId: String!
    $jobId: ID!
    $regionCode: String
  ) {
    explorer(
      projectId: $projectId
      serviceId: $serviceId
      regionCode: $regionCode
    ) {
      job(jobId: $jobId) {
        id
        config
        status
        type
        currentStart
        nextStart
        stats(n: 100) {
          successPct
          runtimes {
            medianDurationUsecs
            p95DurationUsecs
            maxDurationUsecs
          }
        }
        executions(n: 100) {
          startTime
          finishTime
          succeeded
          error {
            errorMessage
            sqlErrorCode
          }
        }
        schedule {
          active
          fixed
          initialStart
          intervalUsecs
          maxRetries
          maxRuntimeUsecs
          retryIntervalUsecs
        }
        proc {
          name
          schema
        }
        target {
          ... on TableID {
            isHypertable
            schema
            name
          }
          ... on ContAggID {
            name
            schema
          }
        }
      }
    }
  }
`;

const GET_OBJECTS_METADATA__TYPE = "getObjectsMetadata";
const GET_OBJECTS_METADATA = gql`
  query GetObjectsMetadata(
    $projectId: ID!
    $serviceId: ID!
    $excludePGTables: Boolean
  ) {
    getObjectsMetadata(
      data: {
        projectId: $projectId
        serviceId: $serviceId
        excludePGTables: $excludePGTables
      }
    ) {
      metadata {
        __typename
        ... on TableMetadata {
          approxRowCount
          owner
          size
          id {
            name
            schema
            isHypertable
          }
        }
        ... on ContAggMetadata {
          approxRowCount
          owner
          size
          parentID {
            ... on TableID {
              schema
              name
            }
            ... on ContAggID {
              schema
              name
            }
          }
          id {
            name
            schema
          }
        }
      }
      hyperMetadata {
        hypertableId
        numChunks
        timeInterval
        timeIntervalUsecs
        compressionStats {
          ... on CompressionStats {
            savings
            compressed
            uncompressed
          }
        }
      }
    }
  }
`;

const GET_COMPRESSION_RECOMMENDATIONS_FOR_SEGMENT_BY_TYPE = `getCompressionRecommendationsForSegmentBy`;
const GET_COMPRESSION_RECOMMENDATIONS_FOR_SEGMENT_BY = gql`
  query GetCompressionRecommendationsForSegmentBy(
    $projectId: ID!
    $serviceId: ID!
    $regionCode: String
    $schema: ID!
    $tableName: ID
    $contAggName: ID
  ) {
    getCompressionRecommendationsForSegmentBy(
      data: {
        projectId: $projectId
        serviceId: $serviceId
        regionCode: $regionCode
        schema: $schema
        tableName: $tableName
        contAggName: $contAggName
      }
    ) {
      availableSegmentByColumns
      recommendedSegmentByColumns
    }
  }
`;

const GET_COMPRESSION_RECOMMENDATIONS_FOR_ORDER_BY_TYPE = `getCompressionRecommendationsForOrderBy`;
const GET_COMPRESSION_RECOMMENDATIONS_FOR_ORDER_BY = gql`
  query GetCompressionRecommendationsForOrderBy(
    $projectId: ID!
    $serviceId: ID!
    $regionCode: String
    $schema: ID!
    $tableName: ID
    $contAggName: ID
    $segmentByColumns: [String!]!
  ) {
    getCompressionRecommendationsForOrderBy(
      data: {
        projectId: $projectId
        serviceId: $serviceId
        regionCode: $regionCode
        schema: $schema
        tableName: $tableName
        contAggName: $contAggName
        segmentByColumns: $segmentByColumns
      }
    ) {
      availableOrderByColumns
      recommendedOrderByColumns {
        name
        descending
        nullsFirst
      }
    }
  }
`;

const GET_FILE_IMPORT__TYPE = "getFileImport";
const GET_FILE_IMPORT = gql`
  query GetFileImport($data: GetFileImportInput!) {
    connectors {
      getFileImport(data: $data) {
        file_import {
          id
          size
          source {
            type
            s3 {
              id
              key
              bucket
              role_arn
            }
            http {
              url
            }
            internal {
              id
            }
          }
          state {
            state
            progress {
              current
              total
              message
            }
            failure_reason
            timelines {
              at
              state
              payload {
                failure_reason
              }
            }
          }
        }
      }
    }
  }
`;

const PREVIEW_SOURCE__TYPE = "previewSource";
const PREVIEW_SOURCE = gql`
  query PreviewSource($data: PreviewSourceInput!) {
    connectors {
      previewSource(data: $data) {
        columns {
          name
          type
          content
        }
        has_header
        bytes
      }
    }
  }
`;

const LIST_BUCKET_OBJECTS__TYPE = "listBucketObjects";
const LIST_BUCKET_OBJECTS = gql`
  query ListBucketObjects($data: ListBucketObjectsInput!) {
    connectors {
      listBucketObjects(data: $data) {
        objects {
          key
          bytes
        }
      }
    }
  }
`;

const LIST_S3_LIVESYNC__TYPE = "listS3LiveSync";
const LIST_S3_LIVESYNC = gql`
  query ListS3LiveSync($projectId: ID!, $serviceId: ID!) {
    connectors {
      listS3LiveSync(data: { project_id: $projectId, service_id: $serviceId }) {
        id
        name
        pattern
        created_at
        updated_at
        enabled
        bucket
        credentials {
          type
          role {
            arn
          }
        }
        definition {
          type
        }
        next_tick
        table_identifier {
          table_name
          schema_name
        }
        frequency
        last_error
      }
    }
  }
`;

const GET_S3_LIVESYNC__TYPE = "getS3LiveSync";
const GET_S3_LIVESYNC = gql`
  query GetS3LiveSync($projectId: ID!, $serviceId: ID!, $id: ID!) {
    connectors {
      getS3LiveSync(
        data: { project_id: $projectId, service_id: $serviceId, id: $id }
      ) {
        id
        name
        pattern
        last_imported_object
        created_at
        updated_at
        enabled
        bucket
        credentials {
          type
          role {
            arn
          }
        }
        settings {
          on_conflict_do_nothing
        }
        definition {
          type
          csv {
            auto_column_mapping
            column_names
            column_mappings {
              source
              destination
            }
            delimiter
            skip_header
          }
          parquet {
            auto_column_mapping
            column_mappings {
              source
              destination
            }
          }
        }
        next_tick
        table_identifier {
          table_name
          schema_name
        }
        frequency
        last_error
      }
    }
  }
`;

const LIST_FILE_IMPORT__TYPE = "listFileImport";
const LIST_FILE_IMPORT = gql`
  query ListFileImport($data: ListFileImportInput!) {
    connectors {
      listFileImport(data: $data) {
        file_imports {
          id
          created_at
          source {
            type
            s3 {
              key
            }
          }
          definition {
            type
          }
          table_identifier {
            table_name
            schema_name
          }
          state {
            progress {
              current
              message
              total
            }
            state
            failure_reason
          }
          size
        }
        page_info {
          end_cursor
          has_next_page
        }
      }
    }
  }
`;
const GET_S3_LIVE_SYNC_AUDIT__TYPE = "getS3LiveSyncAudit";
const GET_S3_LIVE_SYNC_AUDIT = gql`
  query getS3LiveSyncAudit($data: GetS3LiveSyncAuditInput!) {
    connectors {
      getS3LiveSyncAudit(data: $data) {
        entries {
          at
        }
      }
    }
  }
`;

const GET_S3_LIVESYNC_STATISTICS__TYPE = "getS3LiveSyncStatistics";
const GET_S3_LIVESYNC_STATISTICS = gql`
  query GetS3LiveSyncStatistics($data: GetS3LiveSyncStatisticsInput!) {
    connectors {
      getS3LiveSyncStatistics(data: $data) {
        statistics {
          total_size_bytes
          total_failed_objects
          state_counts {
            state
            count
          }
        }
      }
    }
  }
`;

const GET_CONNECTORS__TYPE = "getConnectors";
const GET_CONNECTORS = gql`
  query GetConnectors(
    $projectId: String!
    $serviceId: String!
    $connectorType: ConnectorType!
  ) {
    connectors {
      getConnectors(
        data: {
          projectId: $projectId
          serviceId: $serviceId
          connectorType: $connectorType
        }
      ) {
        connectors {
          displayName
          created
          state
          stateMessage
          kafkasrc {
            id
            enabled
          }
          icebergdst {
            id
            catalogS3Tables {
              bucketArn
              iamRoleArn
            }
            catalogRest {
              uri
              warehouse
              clientId
              scope
              oauth2ServerUri
              vendedCredentials
              additionalHeaders
            }
            status {
              lastCommittedTxnTime
              metricsUpdatedAt
              throughput
              totalRecords
              bytesRead
              confirmedFlushLsn
              restartLsn
              sourceWalLsn
            }
          }
          pgsrc {
            id
            enabled
            status {
              lastReplicatedTxnTime
            }
          }
        }
      }
    }
  }
`;

const GET_SOURCE_CONNECTORS_KAFKA__TYPE = "kafkaConnectors";
const GET_SOURCE_CONNECTORS_POSTGRES__TYPE = "postgresConnectors";
const GET_SOURCE_CONNECTORS_S3__TYPE = "s3Connectors";
const GET_SOURCE_CONNECTORS = gql`
  query GetSourceConnectors(
    $projectId: String!
    $serviceId: String!
    $s3ProjectId: ID!
    $s3ServiceId: ID!
  ) {
    connectors {
      s3Connectors: listS3LiveSync(
        data: { project_id: $s3ProjectId, service_id: $s3ServiceId }
      ) {
        id
        name
        pattern
        created_at
        updated_at
        enabled
        bucket
        credentials {
          type
          role {
            arn
          }
        }
        definition {
          type
        }
        next_tick
        table_identifier {
          table_name
          schema_name
        }
        frequency
        last_error
      }
      kafkaConnectors: getConnectors(
        data: {
          projectId: $projectId
          serviceId: $serviceId
          connectorType: kafka_src
        }
      ) {
        connectors {
          displayName
          created
          state
          stateMessage
          kafkasrc {
            id
            enabled
          }
        }
      }
      postgresConnectors: getConnectors(
        data: {
          projectId: $projectId
          serviceId: $serviceId
          connectorType: pg_src
        }
      ) {
        connectors {
          displayName
          created
          state
          stateMessage
          pgsrc {
            id
            enabled
            status {
              lastReplicatedTxnTime
            }
          }
        }
      }
    }
  }
`;

const GET_CONNECTOR__TYPE = "getConnector";
const GET_CONNECTOR = gql`
  query GetConnector($data: GetConnectorInput!) {
    connectors {
      getConnector(data: $data) {
        connector {
          displayName
          created
          state
          stateMessage
          kafkasrc {
            id
            kafkaClusterConfigId
            schemaRegistryConfigId
            kafkaTopic
            tableSchema
            tableName
            enabled
          }
          s3tabledst {
            id
            enabled
          }
          pgsrc {
            id
            sourceConfigId
            enabled
            tableSyncWorkers
            publicationNames
            status {
              lastReplicatedTxnTime
              lastReplicatedLsn
              lastReplicatedLsnStr
              sourceFlushLsn
              sourceFlushLsnStr
              appliedOperationsCount
              metricsUpdatedAt
              ready
            }
            createdAt
          }
        }
      }
    }
  }
`;

const GET_KAFKA_TOPICS_FROM_CLUSTER__TYPE = "getKafkaTopicsFromCluster";
const GET_KAFKA_TOPICS_FROM_CLUSTER = gql`
  query GetKafkaTopicsFromCluster($data: GetKafkaTopicsFromClusterInput!) {
    connectors {
      getKafkaTopicsFromCluster(data: $data) {
        topics {
          name
        }
      }
    }
  }
`;

const GET_KAFKA_TOPIC_SCHEMA_MAPPING__TYPE = "getKafkaTopicSchemaMapping";
const GET_KAFKA_TOPIC_SCHEMA_MAPPING = gql`
  query GetKafkaTopicSchemaMapping($data: GetKafkaTopicSchemaMappingInput!) {
    connectors {
      getKafkaTopicSchemaMapping(data: $data) {
        topicDetails {
          topicName
          schemaFormat
          fields {
            fieldName
            fieldType
          }
          schemaDefinition {
            schemaId
            definition
          }
        }
        mappedTableDetails {
          tableSchema
          tableName
          fields {
            fieldName
            fieldType
          }
        }
      }
    }
  }
`;

const GET_KAFKA_STATUS__TYPE = "getKafkaSrcConnectorStatus";
const GET_KAFKA_STATUS = gql`
  query GetKafkaSrcConnectorStatus($data: GetKafkaSrcConnectorStatusInput!) {
    connectors {
      getKafkaSrcConnectorStatus(data: $data) {
        status {
          id
          messagesProcessedTotal
          bytesIngestedTotal
          consumerLagMessages
          recordErrorCount
          operationalErrorCount
          lastMessageProcessedAt
          lastMessageEventTimestamp
          updated
          ready
        }
      }
    }
  }
`;

const GET_PG_SOURCE_PUBLICATIONS__TYPE = "getPgSrcPublications";
const GET_PG_SOURCE_PUBLICATIONS = gql`
  query GetPgSrcPublications($projectId: String!, $sourceId: String!) {
    connectors {
      getPgSrcPublications(
        data: { projectId: $projectId, sourceId: $sourceId }
      ) {
        publications {
          publicationName
        }
      }
    }
  }
`;

const GET_PG_SOURCE_PUBLICATION_TABLES__TYPE = "getPgSrcPublicationTables";
const GET_PG_SOURCE_PUBLICATION_TABLES = gql`
  query GetPgSrcPublicationTables(
    $projectId: String!
    $sourceId: String!
    $publicationName: String!
  ) {
    connectors {
      getPgSrcPublicationTables(
        data: {
          projectId: $projectId
          sourceId: $sourceId
          publicationName: $publicationName
        }
      ) {
        tables {
          table {
            schemaName
            tableName
          }
        }
      }
    }
  }
`;

const GET_PG_SOURCE_SCHEMA__TYPE = "getPgSrcSchemas";
const GET_PG_SOURCE_SCHEMA = gql`
  query GetPgSrcSchemas($projectId: String!, $sourceId: String!) {
    connectors {
      getPgSrcSchemas(data: { projectId: $projectId, sourceId: $sourceId }) {
        schemas
      }
    }
  }
`;

const GET_PG_SRC_TABLES__TYPE = "getPgSrcTables";
const GET_PG_SRC_TABLES = gql`
  query GetPgSrcTables(
    $projectId: String!
    $schemaName: String!
    $sourceId: String!
  ) {
    connectors {
      getPgSrcTables(
        data: {
          projectId: $projectId
          schemaName: $schemaName
          sourceId: $sourceId
        }
      ) {
        tables {
          table {
            schemaName
            tableName
          }
          columns {
            name
            type
          }
          approximateRows
          approximateSize
          hypertableSpec {
            primaryDimension {
              columnName
              partitionInterval
            }
          }
        }
      }
    }
  }
`;

const GET_PG_SRC_TARGET_TABLES__TYPE = "getPgSrcConnectorTargetTables";
const GET_PG_SRC_TARGET_TABLES = gql`
  query GetPgSrcTargetTables(
    $projectId: String!
    $serviceId: String!
    $connectorId: String!
  ) {
    connectors {
      getPgSrcConnectorTargetTables(
        data: {
          projectId: $projectId
          serviceId: $serviceId
          connectorId: $connectorId
        }
      ) {
        tables {
          table {
            table {
              schemaName
              tableName
            }
            tableMapping {
              schemaName
              tableName
            }
            hypertableSpec {
              primaryDimension {
                columnName
                partitionInterval
              }
            }
          }
          state
          rowsCopied
          bytesCopied
          approximateRows
          approximateSize
          lastError
        }
      }
    }
  }
`;

const GET_CONNECTOR_TARGET_TABLES__TYPE = "getConnectorTargetTables";
const GET_CONNECTOR_TARGET_TABLES = gql`
  query GetConnectorTargetTables(
    $projectId: String!
    $serviceId: String!
    $connectorId: String!
  ) {
    connectors {
      getConnectorTargetTables(
        data: {
          projectId: $projectId
          serviceId: $serviceId
          connectorId: $connectorId
        }
      ) {
        icebergdst {
          tables {
            table {
              schemaName
              tableName
            }
            state
            lastError
            snapshotStartedAt
            snapshotCompletedAt
            snapshotAttemptCount
            icebergSchema
            icebergTable
            rowsCopied
            approximateRows
          }
        }
      }
    }
  }
`;

const GET_SSH_TUNNEL_CONFIG__TYPE = "getSSHTunnelConfig";
const GET_SSH_TUNNEL_CONFIG = gql`
  query GetSSHTunnelConfig($data: GetSSHTunnelConfigInput!) {
    connectors {
      getSSHTunnelConfig(data: $data) {
        sshTunnelConfig {
          sshTunnelId
          name
          host
          port
          username
          publicKey
          projectId
          createdAt
          updatedAt
        }
      }
    }
  }
`;

const GET_SSH_TUNNEL_CONFIGS__TYPE = "getSSHTunnelConfigs";
const GET_SSH_TUNNEL_CONFIGS = gql`
  query GetSSHTunnelConfigs($data: GetSSHTunnelConfigsInput!) {
    connectors {
      getSSHTunnelConfigs(data: $data) {
        sshTunnelConfigs {
          sshTunnelId
          name
          host
          port
          username
          publicKey
          projectId
          createdAt
          updatedAt
        }
      }
    }
  }
`;

const VERIFY_SSH_KEY_PAIR__TYPE = "verifySSHTunnelConfig";
const VERIFY_SSH_KEY_PAIR = gql`
  query VerifySSHTunnelConfig($data: VerifySSHTunnelConfigInput!) {
    connectors {
      verifySSHTunnelConfig(data: $data) {
        success
      }
    }
  }
`;

const GET_PG_SRC_CONFIG__TYPE = "getPgSrcConfig";
const GET_PG_SRC_CONFIG = gql`
  query GetPgSrcConfig($data: GetPgSrcConfigInput!) {
    connectors {
      getPgSrcConfig(data: $data) {
        sourceConfig {
          sourceId
          projectId
          name
          sshTunnelId
          connectionString
          createdAt
          updatedAt
        }
      }
    }
  }
`;

const GET_VIRTUAL_PLANS__TYPE = "getVirtualPlans";
const GET_VIRTUAL_PLANS = gql`
  query GetVirtualPlans {
    getVirtualPlans {
      plans {
        kind
      }
    }
  }
`;

const GET_PLAN_DOWNGRADE_CHECK__TYPE = "getPlanDowngradeCheck";
const GET_PLAN_DOWNGRADE_CHECK = gql`
  query GetPlanDowngradeCheck($projectId: ID!, $downgradeTo: VirtualPlanType!) {
    downgradeCheck(data: { projectID: $projectId, downgradeTo: $downgradeTo }) {
      dependencies
    }
  }
`;

const GET_CURRENT_COST__TYPE = "getCurrentCost";
const GET_CURRENT_COST = gql`
  ${BILLING_ACCOUNT_COST_INFO_FRAGMENT}
  query GetCurrentCost($projectId: ID, $billingAccountId: ID) {
    getCurrentCost(
      data: { projectId: $projectId, billingAccountId: $billingAccountId }
    ) {
      ...BillingAccountCostInfoFragment
      serviceCostInfos {
        serviceID
        costToDateGross
        productCostInfos {
          productID
          productName
          costToDate
        }
      }
    }
  }
`;

const GET_MONTH_REMAINDER_ESTIMATE__TYPE = "getMonthRemainderEstimate";
const GET_MONTH_REMAINDER_ESTIMATE = gql`
  query GetMonthRemainderEstimate($projectId: ID, $billingAccountId: ID) {
    getMonthRemainderEstimate(
      data: { projectId: $projectId, billingAccountId: $billingAccountId }
    )
  }
`;

const GET_RECOVERY_PROGRESS = gql`
  query GetRecoveryProgress($projectId: ID!, $serviceId: ID!) {
    getRecoveryProgress(projectId: $projectId, serviceId: $serviceId) {
      recorded
      ordinal
      lag
    }
  }
`;

const GET_RECOMMENDATIONS__TYPE = "getRecommendations";
const GET_RECOMMENDATIONS = gql`
  query GetRecommendations($projectId: ID!, $serviceId: ID) {
    getRecommendations(data: { projectId: $projectId, serviceId: $serviceId }) {
      id
      projectId
      serviceId
      resourceId
      message
      created
      updated
      message
      type
      actionText
      category
      title
      metadata {
        ... on HypertableChangeChunkTimeIntervalMetadata {
          tableName
          schemaName
          # currentIntervalSeconds
          recommendedIntervalSeconds
        }
      }
    }
  }
`;

const GET_ACTIVITY_LOG = gql`
  query GetActivityLog(
    $projectId: String!
    $serviceId: String
    $start: Time
    $end: Time
    $limit: Int
  ) {
    activityLog(
      projectId: $projectId
      serviceId: $serviceId
      start: $start
      end: $end
      limit: $limit
    ) {
      items {
        item {
          id
          timestamp
          eventType
          serviceId
          description
          user {
            name
            email
          }
        }
      }
    }
  }
`;

const GET_CHUNK_TUNING_STATUS__TYPE = "dbagent";
const GET_CHUNK_TUNING_STATUS = gql`
  query GetChunkTuningStatus(
    $projectId: String!
    $serviceId: String!
    $hypertableId: Int!
  ) {
    dbagent(projectId: $projectId, serviceId: $serviceId) {
      getChunkTuningStatus(hypertableId: $hypertableId) {
        enabled
        recommendedInterval
        preTunerInterval
      }
    }
  }
`;

const LIST_PRIVATE_LINKS_AUTHORIZATIONS_CONNECTIONS = gql`
  query ListPrivateLinksAuthorizationsConnections($projectId: ID!) {
    connections: listPrivateLinkConnections(data: { projectId: $projectId }) {
      connectionId
      principalId
      cloudProvider
      linkIdentifier
      state
      ipAddress
      name
      providerConnectionId
      region
      createdAt
      bindings {
        projectId
        serviceId
        connectionId
        bindingType
        port
        hostname
        createdAt
      }
    }
  }
`;

const LIST_PRIVATE_LINK_CONNECTIONS = gql`
  query ListPrivateLinkConnections($projectId: ID!) {
    connections: listPrivateLinkConnections(data: { projectId: $projectId }) {
      connectionId
      principalId
      cloudProvider
      linkIdentifier
      state
      ipAddress
      name
      providerConnectionId
      region
      createdAt
      bindings {
        projectId
        serviceId
        connectionId
        bindingType
        port
        hostname
        createdAt
      }
    }
  }
`;

const LIST_PRIVATE_LINK_REGIONS = gql`
  query ListPrivateLinkRegions {
    regions: listPrivateLinkAvailableRegions {
      region
      cloudProvider
      serviceName
    }
  }
`;

const GET_PRIVATE_LINK_SERVICE_CONNECTIONS = gql`
  query GetPrivateLinkServiceConnections($projectId: ID!, $serviceId: ID!) {
    connections: getPrivateLinkServiceConnections(
      data: { projectId: $projectId, serviceId: $serviceId }
    ) {
      connections {
        connectionId
        principalId
        cloudProvider
        linkIdentifier
        state
        ipAddress
        name
        providerConnectionId
        region
        createdAt
        bindings {
          projectId
          serviceId
          connectionId
          bindingType
          port
          hostname
          createdAt
        }
      }
    }
  }
`;

const GET_SUPPORT_CASES__TYPE = "getSupportCases";
const GET_SUPPORT_CASES = gql`
  query GetSupportCases($projectId: ID!) {
    getSupportCases(data: { projectId: $projectId }) {
      id
      caseNumber
      subject
      isClosed
      createdDate
      suppliedEmail
      impact
      isProduction
      messages {
        fromName
        incoming
        textBody
        htmlBody
        messageDate
        attachments {
          contentDocumentId
          title
          fileExtension
          contentSize
          url
        }
      }
    }
  }
`;

const LIST_BACKUPS__TYPE = "listBackups";
const GET_CROSS_REGION_BACKUP_STATUS = gql`
  query GetCrossRegionBackupsStatus($projectId: ID!, $serviceId: ID!) {
    crossRegionBackupsStatusV2(
      data: { projectId: $projectId, serviceId: $serviceId }
    ) {
      enabled
      regions {
        region
        createdAt
      }
    }
  }
`;

const GET_BACKUPS_RETENTION_STATUS = gql`
  query GetBackupsRetentionStatus($projectId: ID!, $serviceId: ID!) {
    backupsRetentionStatus(
      data: { projectId: $projectId, serviceId: $serviceId }
    ) {
      retType
      retCount
    }
  }
`;

const LIST_FORK_REGIONS = gql`
  query ListForkRegions($projectId: ID!, $serviceId: ID!) {
    listForkRegions(data: { projectId: $projectId, serviceId: $serviceId }) {
      regions
    }
  }
`;

const GET_BILLING_TIMELINE__TYPE = "billingTimeline";
const GET_BILLING_TIMELINE = gql`
  query billingTimeline($billingAccountId: ID!) {
    billingTimeline(billingAccountId: $billingAccountId) {
      currency
      timelineBegin
      timelineEnd
      dailyCompositions {
        date
        total
        products {
          productId
          productName
          cost
        }
      }
    }
  }
`;

const USER_SESSIONS = gql`
  query UserSessions {
    userSessions {
      sid
      deviceType
      deviceName
      ipAddress
      country
      city
      created
      lastUsed
      isCurrent
    }
  }
`;

const USER_SESSIONS__TYPE = "userSessions";

export {
  GET_SUPPORT_CASES,
  GET_SUPPORT_CASES__TYPE,
  GET_PROJECT,
  GET_PROJECT__TYPE,
  GET_USER,
  GET_USER__TYPE,
  GET_ALL_PROJECTS,
  GET_ALL_PROJECTS__TYPE,
  GET_BILLING_ACCOUNT,
  GET_BILLING_EMAILS,
  GET_BILLING_ACCOUNT__TYPE,
  GET_POPSQL_INTEGRATION_STATUS__TYPE,
  GET_POPSQL_INTEGRATION_STATUS,
  GET_PROJECT_MEMBERS,
  GET_PROJECT_INVITES,
  GET_PROJECT_INVITES_FOR_USER,
  GET_ORB_INVOICES,
  GET_ORB_INVOICES__TYPE,
  GET_PAYMENT_METHODS,
  GET_PAYMENT_METHODS__TYPE,
  GET_SERVICE,
  GET_SERVICE__TYPE,
  GET_ALL_SERVICES,
  GET_SERVICE_RESOURCES_AND_STATUS,
  GET_SERVICE_RESIZE_INFO,
  GET_ALL_SERVICES__TYPE,
  GET_SERVICE_LOGS__TYPE,
  GET_SERVICE_LOGS,
  SERVICE_LOGS_PAGE,
  GET_SERVICE_CONFIG_HISTORY,
  GET_SERVICE_GENERAL_INFORMATION,
  GET_SERVICE_GENERAL_INFORMATION__TYPE,
  GET_SERVICE_TABLES,
  GET_SERVICE_CATALOG_TABLES,
  GET_SERVICE_CATALOG_TABLES__TYPE,
  GET_SERVICE_CAGG_INFO,
  GET_SERVICE_CAGG_INFO__TYPE,
  GET_SERVICE_CONTINUOUS_AGGREGATES,
  GET_SERVICE_CONTINUOUS_AGGREGATES__TYPE,
  GET_SERVICE_LARGEST_OBJECTS,
  GET_SERVICE_LARGEST_OBJECTS__TYPE,
  GET_SERVICE_TABLE_INFO,
  GET_SERVICE_TABLE_INFO__TYPE,
  GET_SERVICE_POSTGRES_AND_TIMESCALEDB_VERSIONS,
  GET_SERVICE_POSTGRES_MAJOR_VERSION_UPGRADE_STATUS,
  GET_POSTGRES_PARAMETERS,
  GET_POSTGRES_PARAMETERS__TYPE,
  GET_ALL_VPC,
  GET_ALL_IP_ALLOW_LIST,
  LOGIN_BASIC,
  LOGIN_MFA_CHALLENGE,
  MFA_CHALLENGE,
  LOGOUT,
  VALIDATE_RESET_TOKEN_MFA,
  GET_PENDING_PAYMENT_DELETION,
  GET_PENDING_PAYMENT_DELETION__TYPE,
  GET_ALL_METRIC_EXPORTERS,
  GET_ALL_METRIC_EXPORTERS__TYPE,
  GET_ALL_GENERIC_EXPORTERS,
  GET_ALL_GENERIC_EXPORTERS__TYPE,
  GET_PAT_RECORDS,
  GET_PAT_RECORDS__TYPE,
  GET_POSTGRES_AVAILABLE_EXTENSIONS,
  GET_QUERY_STATS,
  GET_QUERY_STATS__TYPE,
  GET_DATA_TIERING_SUMMARY,
  GET_DATA_TIERING_SUMMARY__TYPE,
  GET_COSTED_PROJECT_DATA_TIERING_USAGE_DISTRIBUTION,
  GET_COSTED_PROJECT_DATA_TIERING_USAGE_DISTRIBUTION__TYPE,
  GET_RECOVERY_WINDOWS,
  GET_RECOVERY_WINDOWS__TYPE,
  GET_QUERY_METRICS_GRAPHS,
  GET_QUERY_METRICS_GRAPHS__TYPE,
  GET_QUERY_DETAILS,
  GET_QUERY_DETAILS__TYPE,
  GET_LATEST_EXPLAIN,
  GET_LATEST_EXPLAIN__TYPE,
  GET_QUERY_METRICS_DETAILS_AND_EXPLAIN,
  GET_QUERY_METRICS_DETAILS_AND_EXPLAIN__TYPE,
  GET_CONNECTION_DETAILS__TYPE,
  GET_CONNECTION_DETAILS,
  GET_CONNECTION_AGGREGATES,
  GET_HISTORICAL_CONNECTIONS,
  GET_HISTORICAL_CONNECTIONS_NEW,
  GET_JOB_LIST,
  GET_OBJECTS_METADATA__TYPE,
  GET_OBJECTS_METADATA,
  GET_COMPRESSION_RECOMMENDATIONS_FOR_SEGMENT_BY_TYPE,
  GET_COMPRESSION_RECOMMENDATIONS_FOR_SEGMENT_BY,
  GET_COMPRESSION_RECOMMENDATIONS_FOR_ORDER_BY_TYPE,
  GET_COMPRESSION_RECOMMENDATIONS_FOR_ORDER_BY,
  GET_FILE_IMPORT__TYPE,
  GET_FILE_IMPORT,
  GET_VIRTUAL_PLANS__TYPE,
  GET_VIRTUAL_PLANS,
  GET_PLAN_DOWNGRADE_CHECK__TYPE,
  GET_PLAN_DOWNGRADE_CHECK,
  GET_ORB_PRODUCTS,
  GET_ORB_ALL_PRODUCTS,
  GET_CURRENT_COST,
  GET_CURRENT_COST__TYPE,
  GET_BILLING_TIMELINE__TYPE,
  GET_BILLING_TIMELINE,
  GET_MONTH_REMAINDER_ESTIMATE,
  GET_MONTH_REMAINDER_ESTIMATE__TYPE,
  GET_RECOVERY_PROGRESS,
  GET_RECOMMENDATIONS__TYPE,
  GET_RECOMMENDATIONS,
  GET_JOB_BY_ID,
  GET_CONNECTORS,
  GET_CONNECTORS__TYPE,
  GET_SOURCE_CONNECTORS,
  GET_SOURCE_CONNECTORS_S3__TYPE,
  GET_SOURCE_CONNECTORS_KAFKA__TYPE,
  GET_SOURCE_CONNECTORS_POSTGRES__TYPE,
  GET_CONNECTOR,
  GET_CONNECTOR__TYPE,
  GET_PG_SRC_CONFIG,
  GET_PG_SRC_CONFIG__TYPE,
  GET_KAFKA_TOPICS_FROM_CLUSTER,
  GET_KAFKA_TOPICS_FROM_CLUSTER__TYPE,
  GET_KAFKA_TOPIC_SCHEMA_MAPPING,
  GET_KAFKA_TOPIC_SCHEMA_MAPPING__TYPE,
  GET_KAFKA_STATUS,
  GET_KAFKA_STATUS__TYPE,
  GET_PG_SOURCE_PUBLICATIONS__TYPE,
  GET_PG_SOURCE_PUBLICATIONS,
  GET_PG_SOURCE_PUBLICATION_TABLES__TYPE,
  GET_PG_SOURCE_PUBLICATION_TABLES,
  GET_PG_SOURCE_SCHEMA__TYPE,
  GET_PG_SOURCE_SCHEMA,
  GET_PG_SRC_TABLES__TYPE,
  GET_PG_SRC_TABLES,
  GET_PG_SRC_TARGET_TABLES__TYPE,
  GET_PG_SRC_TARGET_TABLES,
  GET_CONNECTOR_TARGET_TABLES__TYPE,
  GET_CONNECTOR_TARGET_TABLES,
  GET_SSH_TUNNEL_CONFIG__TYPE,
  GET_SSH_TUNNEL_CONFIG,
  GET_SSH_TUNNEL_CONFIGS__TYPE,
  GET_SSH_TUNNEL_CONFIGS,
  VERIFY_SSH_KEY_PAIR__TYPE,
  VERIFY_SSH_KEY_PAIR,
  PREVIEW_SOURCE__TYPE,
  PREVIEW_SOURCE,
  LIST_BUCKET_OBJECTS__TYPE,
  LIST_BUCKET_OBJECTS,
  GET_ALL_NEXT_MAINTENANCE,
  LIST_S3_LIVESYNC__TYPE,
  LIST_S3_LIVESYNC,
  GET_S3_LIVESYNC__TYPE,
  GET_S3_LIVESYNC,
  GET_S3_LIVE_SYNC_AUDIT__TYPE,
  GET_S3_LIVE_SYNC_AUDIT,
  GET_S3_LIVESYNC_STATISTICS__TYPE,
  GET_S3_LIVESYNC_STATISTICS,
  LIST_FILE_IMPORT__TYPE,
  LIST_FILE_IMPORT,
  GET_SERVICE_METRICS,
  GET_SERVICE_METRICS_QPS,
  GET_ACTIVITY_LOG,
  GET_CHUNK_TUNING_STATUS,
  GET_CHUNK_TUNING_STATUS__TYPE,
  LIST_PRIVATE_LINKS_AUTHORIZATIONS_CONNECTIONS,
  LIST_PRIVATE_LINK_CONNECTIONS,
  LIST_PRIVATE_LINK_REGIONS,
  GET_PRIVATE_LINK_SERVICE_CONNECTIONS,
  GET_CROSS_REGION_BACKUP_STATUS,
  GET_BACKUPS_RETENTION_STATUS,
  LIST_FORK_REGIONS,
  LIST_BACKUPS__TYPE,
  USER_SESSIONS,
  USER_SESSIONS__TYPE,
};
